import { motion } from "framer-motion";
import { PHOTOGRAPHY } from "@/lib/data";

export default function Photography() {
  return (
    <section
      id="photography"
      data-testid="photography-section"
      className="relative bg-[#050505] py-24 md:py-40 border-t border-white/10"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        {/* Section label */}
        <div className="flex items-center gap-3 mb-10">
          <span className="font-mono-label text-[10px] text-[#C4A484]">
            003 · Photography
          </span>
          <span className="flex-1 h-px bg-white/10" />
        </div>

        {/* Editorial header */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-20">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
            className="md:col-span-8 font-heading text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.9]"
          >
            Frames that
            <br />
            outlive the
            <br />
            <span className="italic text-[#C4A484]">wedding day.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.15 }}
            viewport={{ once: true }}
            className="md:col-span-4 md:pt-6 text-[#8F8F8F] text-sm leading-relaxed"
          >
            Photography by Flyora — a still archive of the day, printed for the
            walls of your home and passed down as the family heirloom your
            grandchildren will hold in their hands.
          </motion.p>
        </div>

        {/* Editorial masonry — CSS columns handle mixed portrait/landscape gracefully */}
        <div className="columns-1 md:columns-2 lg:columns-3 gap-4 md:gap-6 [column-fill:_balance]">
          {PHOTOGRAPHY.map((item, i) => (
            <PhotoCard
              key={item.id}
              item={item}
              index={String(i + 1).padStart(2, "0")}
            />
          ))}
        </div>

        {/* Editorial footer note */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
          className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-12 gap-10 border-t border-white/10 pt-10"
        >
          <div className="md:col-span-4 font-mono-label text-[10px] text-[#8F8F8F]">
            Format · 35mm · Medium Format · Digital
          </div>
          <div className="md:col-span-4 font-mono-label text-[10px] text-[#8F8F8F]">
            Delivery · Hand-crafted album · Fine art prints
          </div>
          <div className="md:col-span-4 font-mono-label text-[10px] text-[#C4A484]">
            <a href="#enquire" className="link-underline" data-testid="photography-cta">
              Request the photography deck →
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function PhotoCard({ item, index }) {
  return (
    <motion.figure
      data-testid={`photo-${item.id}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, ease: [0.65, 0, 0.35, 1] }}
      viewport={{ once: true, margin: "-5%" }}
      className="relative group overflow-hidden break-inside-avoid mb-4 md:mb-6"
    >
      <img
        src={item.src}
        alt={item.caption}
        loading="lazy"
        className="w-full h-auto object-cover transition-transform duration-[1800ms] ease-out group-hover:scale-105"
      />
      {/* Subtle bottom gradient for caption legibility */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />

      {/* Index number, top-left */}
      <figcaption className="absolute top-4 left-4 font-mono-label text-[10px] text-white/80 mix-blend-difference">
        {index}
      </figcaption>

      {/* Caption on hover */}
      <figcaption className="absolute bottom-5 left-5 right-5 flex items-end justify-between opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-700 pointer-events-none">
        <span className="font-heading text-2xl md:text-3xl tracking-tighter text-[#FDFBF7]">
          {item.caption}
        </span>
        <span className="font-mono-label text-[10px] text-[#C4A484] pb-1 hidden md:inline">
          Flyora
        </span>
      </figcaption>
    </motion.figure>
  );
}
